/*
 * XOVIST approximate local-currency prices, display only (v5, 2026-10-10)
 * ------------------------------------------------------------------
 * Why:     checkout is always in US dollars (Whop charges USD; multi-currency checkout needs Shopify
 *          Payments, which this store does not have). A shopper abroad gets a small muted approximate
 *          price in their own currency, written their way (U+2248 then e.g. "GBP 26.80" as a UK shopper
 *          writes it), next to the USD prices, and one line under the cart total saying they are charged
 *          in USD. Nothing else changes: prices, totals, inputs and the cart are never touched.
 * Country: the visitor's detected country, from the one lookup assets/xv-card-gate.js already makes
 *          (xvCardGate.onSettle / .country(); without the gate, only its sessionStorage cache
 *          'xv_card_geo'). If there is none, the localization country (data-store-country) when it is not
 *          the store default (data-default-country, US). US or unknown: nothing is shown. This file makes
 *          no geo request of its own. Only while the page shows USD (Shopify.currency.active): if prices
 *          are ever shown in another currency, the "$" amounts are not US dollars, so nothing is shown.
 * Rates:   GET FX_URL (Frankfurter, ECB rates, base USD) at most once a day per browser, cached in
 *          localStorage 'xv_fx_usd' with a timestamp. A failed fetch waits an hour before the next try and
 *          keeps using a cached rate up to 7 days old. Frankfurter has no AED, BHD, PKR or TWD: those
 *          use FALLBACK below. No rate: nothing is shown.
 * Where (markup checked on the draft theme, 2026-10-10):
 *   - product price and compare-at (snippets/product-info.liquid 'price' block): after
 *     .shop-product-price-block and .shop-compare-price; they follow the quantity-break selection;
 *     each label takes its price's flex `order` (assets/xv-pdp.css reorders that row), see inRow();
 *     the compare-at label is struck through like the compare-at price, so it never reads as a price;
 *   - quantity-break per-camera prices (snippets/quantity-break.liquid): the "$32.00 each" in each
 *     option's .quantity-break-title gets a line under the title;
 *   - sticky add-to-cart (sections/sticky-add-to-cart.liquid): the bar shows no price today, so
 *     nothing is added; a price element added there later is covered;
 *   - cart lines, drawer (snippets/cart-items.liquid) and /cart (sections/main-cart-items.liquid):
 *     the line price, under it in .cart-item__price-wrapper;
 *   - cart totals (snippets/xv-cart-summary.liquid, drawer and /cart): Subtotal and Total, plus
 *     NOTE once under the summary.
 * Re-applied after cart-drawer:render/update/open, quantity_break:changed, pageshow, load and any
 * DOM change that adds elements or touches a price (MutationObserver: a change in or of a price area runs
 * at once, before paint, with a guard against a script that keeps undoing us; any other added element
 * runs once 30 ms after the first change; the records our own writes cause are dropped). Idempotent: one
 * label per price, labels whose price is gone are removed. If anything throws, every label is removed
 * and it stops.
 */
(function () {
  'use strict';
  if (window.xvLocalCurrency) return;

  var me = document.currentScript || document.getElementById('xv-local-currency-js');
  function attr(n) { return (me && me.getAttribute(n)) || ''; }
  var STORE = attr('data-store-country').toUpperCase();
  var DEFAULT = (attr('data-default-country') || 'US').toUpperCase();

  var EURO = ['DE', 'FR', 'NL', 'BE', 'IT', 'PT', 'LU', 'AT', 'ES', 'SK', 'SI', 'EE', 'LV', 'LT', 'FI', 'MT', 'IE', 'CY', 'GR', 'HR'];
  var CURRENCY = {
    GB: 'GBP', DK: 'DKK', SE: 'SEK', PL: 'PLN', CZ: 'CZK', HU: 'HUF', CH: 'CHF', NO: 'NOK', CA: 'CAD',
    AU: 'AUD', NZ: 'NZD', JP: 'JPY', KR: 'KRW', SG: 'SGD', HK: 'HKD', MY: 'MYR', TW: 'TWD', AE: 'AED',
    BH: 'BHD', PK: 'PKR'
  };
  EURO.forEach(function (c) { CURRENCY[c] = 'EUR'; });
  /* how the shopper writes money at home; a locale that prints a bare "$" falls back to en-US ("CA$") */
  var LOCALE = {
    GB: 'en-GB', IE: 'en-IE', MT: 'en-MT', CY: 'el-CY', DE: 'de-DE', AT: 'de-AT', LU: 'fr-LU', FR: 'fr-FR',
    BE: 'nl-BE', NL: 'nl-NL', IT: 'it-IT', ES: 'es-ES', PT: 'pt-PT', SK: 'sk-SK', SI: 'sl-SI', EE: 'et-EE',
    LV: 'lv-LV', LT: 'lt-LT', FI: 'fi-FI', GR: 'el-GR', HR: 'hr-HR', DK: 'da-DK', SE: 'sv-SE', PL: 'pl-PL',
    CZ: 'cs-CZ', HU: 'hu-HU', CH: 'de-CH', NO: 'nb-NO', CA: 'en-CA', AU: 'en-AU', NZ: 'en-NZ', JP: 'ja-JP',
    KR: 'ko-KR', SG: 'en-SG', HK: 'en-HK', MY: 'en-MY', TW: 'zh-TW', AE: 'en-AE', BH: 'en-BH', PK: 'en-PK'
  };
  var NO_DECIMALS = ['JPY', 'KRW', 'HUF', 'PKR'];
  /* Frankfurter has no AED, BHD, PKR or TWD. Units per 1 USD on FALLBACK_DATE (Frankfurter v2,
     /v2/rates?base=USD). AED and BHD are pegged to the dollar. PKR and TWD float:
     REFRESH THEM EVERY 3 MONTHS (next: January 2027), and update FALLBACK_DATE. */
  var FALLBACK_DATE = '2026-10-10';
  var FALLBACK = { AED: 3.6725, BHD: 0.376, PKR: 277.18, TWD: 31.905 };
  /* the brief's https://api.frankfurter.app/latest?from=USD answers 301 to this URL with no CORS header,
     which a browser treats as a network error; this is where it redirects, same JSON */
  var FX_URL = 'https://api.frankfurter.dev/v1/latest?from=USD';
  var FX_KEY = 'xv_fx_usd';        /* localStorage: {t: fetched at (ms), date, rates: {GBP: 0.75, ...}} */
  var FX_FAIL_KEY = 'xv_fx_fail';  /* localStorage: time of the last failed fetch (ms) */
  var HOUR = 3600000, DAY = 24 * HOUR;
  var FRESH_MS = DAY, STALE_MS = 7 * DAY, RETRY_MS = HOUR;
  var NOTE = 'You pay in US dollars (USD).';   /* 2026-10-10: shortened (owner); was "You'll be charged in US dollars (USD). Your bank converts it." */
  /* 2026-10-11 (v5.2, owner: "in the cart only show in USD"): the cart (drawer and /cart) gets no labels and no
     NOTE; its Total says "USD" itself (snippets/xv-cart-summary.liquid). Product page labels are unchanged.
     true = v5.1 behaviour (cart lines, Subtotal, Total and NOTE labelled again). */
  var CART = false;

  var LC = 'xv-lc';                /* class of every label; the note is xv-lc-note */
  var KEY = 'data-xv-lc';          /* kind of label: pdp, was, qb, satc, line, sub, total, note */
  var WATCH = '.shop-product-price-container, .quantity-break-container, #stickyAddToCart' +
    (CART ? ', .cart-item__price-wrapper, .xv-sum, .xv-sum-block, .xv-sum-wrap' : '');

  var country = '', currency = '', rate = 0, nf = null;
  var started = false, active = false, failed = false;
  var obs = null, timer = 0, touched = [];

  /* ---------- storage (may throw or be empty) ---------- */
  function lget(k) { try { return window.localStorage.getItem(k); } catch (e) { return null; } }
  function lset(k, v) { try { window.localStorage.setItem(k, v); } catch (e) { /* ignore */ } }
  function sget(k) { try { return window.sessionStorage.getItem(k); } catch (e) { return null; } }

  function each(sel, fn, root) {
    var list = (root || document).querySelectorAll(sel);
    for (var i = 0; i < list.length; i++) fn(list[i]);
  }

  /* ---------- country -> currency -> formatter ---------- */
  function pick(detected) {
    var d = String(detected || '').toUpperCase();
    if (/^[A-Z]{2}$/.test(d)) return d;
    if (/^[A-Z]{2}$/.test(STORE) && STORE !== DEFAULT) return STORE;
    return '';
  }
  function makeFormat(loc, cur) {
    var d = NO_DECIMALS.indexOf(cur) > -1 ? 0 : 2;
    var tries = [[loc, 'symbol'], ['en-US', 'symbol'], ['en-US', 'code']];
    for (var i = 0; i < tries.length; i++) {
      try {
        var f = new Intl.NumberFormat(tries[i][0], {
          style: 'currency', currency: cur, currencyDisplay: tries[i][1],
          minimumFractionDigits: d, maximumFractionDigits: d
        });
        var probe = f.format(1234);
        /* ASCII digits, and never a bare "$" that reads like the US price next to it */
        if (probe.indexOf('234') > -1 && !/(^|[^A-Za-z])\$/.test(probe)) return f;
      } catch (e) { /* no Intl or no such currency: try the next */ }
    }
    return null;
  }

  /* ---------- rates ---------- */
  function cleanRates(r) {
    if (!r || typeof r !== 'object') return null;
    var out = {}, n = 0;
    for (var k in r) {
      if (!Object.prototype.hasOwnProperty.call(r, k) || !/^[A-Z]{3}$/.test(k)) continue;
      var v = r[k];
      if (typeof v === 'number' && isFinite(v) && v > 0 && v < 1e7) { out[k] = v; n++; }
    }
    return n ? out : null;
  }
  function readCache() {
    try {
      var o = JSON.parse(lget(FX_KEY) || 'null');
      if (o && typeof o.t === 'number' && isFinite(o.t)) {
        var r = cleanRates(o.rates);
        if (r) return { t: o.t, rates: r };
      }
    } catch (e) { /* unreadable: as if empty */ }
    return null;
  }
  function getRate(cur, cb) {
    var done = false;
    function finish(r) { if (done) return; done = true; cb(r || 0); }
    if (FALLBACK[cur]) { finish(FALLBACK[cur]); return; }
    var now = Date.now(), c = readCache(), age = c ? now - c.t : -1;
    var usable = c && age >= 0 && age < STALE_MS ? c.rates[cur] : 0;
    if (c && age >= 0 && age < FRESH_MS) { finish(usable); return; }
    var failAt = Number(lget(FX_FAIL_KEY)) || 0;
    if (!window.fetch || (failAt && now - failAt >= 0 && now - failAt < RETRY_MS)) { finish(usable); return; }
    fetch(FX_URL, { credentials: 'omit', referrerPolicy: 'no-referrer' })
      .then(function (res) { return res && res.ok ? res.json() : null; })
      .then(function (d) {
        var r = d && d.base === 'USD' ? cleanRates(d.rates) : null;
        if (!r) throw new Error('fx shape');
        lset(FX_KEY, JSON.stringify({ t: Date.now(), date: String(d.date || ''), rates: r }));
        finish(r[cur]);
      })
      .catch(function () { lset(FX_FAIL_KEY, String(Date.now())); finish(usable); });
  }

  /* ---------- reading a USD price off the page ---------- */
  function usd(el) {
    if (!el) return 0;
    var m = String(el.textContent || '').match(/\$\s*([0-9]{1,3}(?:,[0-9]{3})+(?:\.[0-9]{1,2})?|[0-9]+(?:\.[0-9]{1,2})?)(?![0-9])/);
    if (!m) return 0;
    var v = parseFloat(m[1].replace(/,/g, ''));
    return isFinite(v) && v > 0 ? v : 0;
  }
  /* the "approx." sign and a no-break space, built from char codes so the file stays plain ASCII */
  var APPROX = String.fromCharCode(0x2248, 0xa0);
  function label(v, suffix) { return APPROX + nf.format(v * rate) + (suffix || ''); }

  /* ---------- placing labels (one per price; reused, never duplicated) ---------- */
  function make(kind, tag) {
    var el = document.createElement(tag);
    el.className = (kind === 'note' ? LC + '-note' : LC + ' ' + LC + '--' + kind);
    el.setAttribute(KEY, kind);
    return el;
  }
  function set(el, text) {
    if (el.textContent !== text) el.textContent = text;
    touched.push(el);
  }
  function after(anchor, kind, tag, text) {   /* the label is the anchor's next element sibling */
    if (!anchor || !anchor.parentNode) return null;
    var n = anchor.nextElementSibling;
    var el = n && n.getAttribute(KEY) === kind ? n : null;
    if (!el) { el = make(kind, tag); anchor.parentNode.insertBefore(el, anchor.nextSibling); }
    set(el, text);
    return el;
  }
  /* The product price row is a flex row whose items carry an `order` (assets/xv-pdp.css, loaded store-wide:
     compare-at 1, price 2, SAVE 3) and a 9px gap. A label with the default order 0 would show FIRST in the
     row, before both USD prices. So each label takes its price's computed order (it then follows that price,
     DOM order breaking the tie), and keeps more room after it than before it, so it reads with its own price,
     not the next one. Written only when it differs (an attribute change, which no observer here watches). */
  function inRow(el, anchor) {
    if (!el) return;
    var a = window.getComputedStyle(anchor), r = window.getComputedStyle(anchor.parentNode);
    var gap = parseFloat(r.columnGap);
    if (!(gap >= 0)) gap = parseFloat(r.getPropertyValue('gap'));
    var want = 'order:' + (parseInt(a.order, 10) || 0) + ' !important;margin:0 8px 0 ' + (gap >= 5 ? 0 : 5) + 'px !important';
    if (el.getAttribute('style') !== want) el.setAttribute('style', want);
  }
  function inside(box, kind, tag, text) {     /* the label is a direct child of box, at the end */
    var el = null, k = box.children;
    for (var i = 0; i < k.length; i++) if (k[i].getAttribute(KEY) === kind) { el = k[i]; break; }
    if (!el) { el = make(kind, tag); box.appendChild(el); }
    set(el, text);
  }

  function pdp() {
    each('.shop-product-price-container', function (box) {
      var p = box.querySelector('.shop-product-price-block'), c = box.querySelector('.shop-compare-price');
      var v = usd(p), w = usd(c);
      if (v) inRow(after(p, 'pdp', 'span', label(v)), p);
      if (w) inRow(after(c, 'was', 'span', label(w)), c);
    });
  }
  function qb() {
    each('.quantity-break-option-container', function (opt) {
      var head = opt.querySelector('.quantity-break-title-container');
      var t = head && head.querySelector('.quantity-break-title');
      var v = usd(t);
      if (!v) return;
      var each_ = /\$\s*[0-9][0-9,]*(?:\.[0-9]{1,2})?\s*each\b/i.test(t.textContent) ? ' each' : '';
      after(head, 'qb', 'span', label(v, each_));
    });
  }
  function sticky() {   /* a price shown in the bar (none today): a leaf element, not in a button or link */
    each('#stickyAddToCart [class*="price"]', function (el) {
      if (el.children.length || el.closest('button, a, s, del, [' + KEY + ']')) return;
      var v = usd(el);
      if (v) inRow(after(el, 'satc', 'span', label(v)), el);
    });
  }
  function lineEl(w) {
    var f = w.querySelector('.cart-item__final-price') || w.querySelector('dd.price');
    if (f) return f;
    var list = w.querySelectorAll('.price');
    for (var i = 0; i < list.length; i++) if (!list[i].closest('s, del')) return list[i];
    return w.querySelector('.gb-price-drawer');
  }
  function lines() {
    each('.cart-item__price-wrapper', function (w) {
      var v = usd(lineEl(w));
      if (v) inside(w, 'line', 'span', label(v));
    });
  }
  function totals() {
    each('.xv-sum', function (sum) {
      each('.xv-sum__row', function (row) {
        if (row.classList.contains('xv-sum__row--deal') || row.classList.contains('xv-sum__row--addon')) return;
        var v = usd(row.querySelector('.xv-sum__num'));   /* Subtotal; Shipping has no amount */
        if (v) after(row, 'sub', 'div', label(v));
      }, sum);
      var tv = sum.querySelector('.xv-sum__total-value'), t = usd(tv);
      if (!t) return;
      after(tv, 'total', 'span', label(t));
      after(sum, 'note', 'p', NOTE);
    });
  }

  function sweep() {    /* labels whose price is gone, or extras: removed */
    each('[' + KEY + ']', function (el) {
      if (touched.indexOf(el) < 0 && el.parentNode) el.parentNode.removeChild(el);
    });
  }
  function teardown() {
    try { each('[' + KEY + ']', function (el) { if (el.parentNode) el.parentNode.removeChild(el); }); } catch (e) { /* ignore */ }
  }
  function fail() {
    failed = true;
    active = false;
    clearTimeout(timer);
    timer = 0;
    if (obs) { try { obs.disconnect(); } catch (e) { /* ignore */ } obs = null; }
    teardown();
  }

  function apply() {
    if (!active) return;
    touched = [];
    try {
      pdp();
      qb();
      sticky();
      if (CART) { lines(); totals(); }
      sweep();   /* with CART false this also removes any cart label left from an earlier run */
    } catch (e) {
      fail();
      return;
    }
    touched = [];
    if (obs) obs.takeRecords();   /* our own writes never wake the observer */
  }
  /* throttled: one run 30 ms after the first change; a page that keeps changing cannot postpone it */
  function later() {
    if (!active || timer) return;
    timer = setTimeout(function () { timer = 0; apply(); }, 30);
  }
  /* a price area changed: run now, inside the observer callback, so the re-rendered drawer, /cart or price row is
     painted with its labels (with the 30 ms throttle every cart update painted once without them, then the
     lines and the checkout button jumped). Like assets/xv-ship-geo.js. More than 10 runs before a 0 ms timer can
     fire means another script keeps undoing us: fall back to the throttle instead of looping in microtasks. */
  var burst = 0, burstTimer = 0;
  function now() {
    if (!active) return;
    if (++burst > 10) { later(); return; }
    if (!burstTimer) burstTimer = setTimeout(function () { burst = 0; burstTimer = 0; }, 0);
    clearTimeout(timer);
    timer = 0;
    apply();
  }
  /* 2: a price area changed, or an element holding one was added (run now); 1: some other element was added
     (throttled: it may be a price area built in steps); 0: nothing for us */
  function urgency(r) {
    var u = 0, n, i;
    if (r.type === 'childList') {
      for (i = 0; i < r.addedNodes.length; i++) {
        n = r.addedNodes[i];
        if (n.nodeType !== 1) continue;
        u = 1;
        if ((n.matches && n.matches(WATCH)) || (n.querySelector && n.querySelector(WATCH))) return 2;
      }
    }
    var e = r.target && (r.target.nodeType === 1 ? r.target : r.target.parentNode);
    return e && e.closest && e.closest(WATCH) ? 2 : u;
  }

  function addStyle() {
    if (document.getElementById('xv-lc-style')) return;
    var st = document.createElement('style');
    st.id = 'xv-lc-style';
    /* muted, about 0.85em, never the accent colour; explicit letter-spacing (the body's -2px trap) */
    st.textContent =
      'html body .xv-lc:not(#_){display:inline-block !important;margin:0 0 0 6px !important;padding:0 !important;' +
      'border:0 !important;background:none !important;font-size:.85em !important;line-height:1.3 !important;' +
      'font-weight:400 !important;font-style:normal !important;letter-spacing:-.005em !important;' +
      'text-transform:none !important;text-decoration:none !important;color:rgba(17,17,17,.62) !important;' +
      'white-space:nowrap !important;font-variant-numeric:tabular-nums;}' +
      /* the compare-at's label is the old price too: struck like it, so "approx. GBP 45.35" never reads as a price */
      'html body .xv-lc--was:not(#_){text-decoration:line-through !important;}' +
      'html body .xv-lc--qb:not(#_){display:block !important;margin:2px 0 0 !important;text-align:left !important;}' +
      'html body .xv-lc--line:not(#_){display:block !important;margin:2px 0 0 !important;text-align:right !important;}' +
      'html body .xv-lc--sub:not(#_){display:block !important;margin:-4px 0 0 !important;text-align:right !important;}' +
      'html body .xv-lc--total:not(#_){display:block !important;flex:0 0 100% !important;margin:-6px 0 0 !important;text-align:right !important;}' +
      'html body .xv-lc-note:not(#_){display:block !important;margin:8px 0 0 !important;padding:0 !important;' +
      'font-size:12px !important;line-height:1.4 !important;font-weight:400 !important;font-style:normal !important;' +
      'letter-spacing:-.005em !important;text-transform:none !important;color:rgba(17,17,17,.62) !important;text-align:left !important;}' +
      /* /cart from 750px (assets/component-cart-items.css): a line price that is the only child of its wrapper gets
         margin-top:1rem. The label makes it a non-only child, so the price would jump 10px up; this keeps it where
         it was. A rule of its own: a browser without :has() drops only this rule (and the price moves up). */
      '@media screen and (min-width:750px){html body cart-items .cart-item__price-wrapper>:first-child' +
      ':not(.cart-item__discounted-prices):not([' + KEY + ']):has(+[' + KEY + '="line"]:last-child){margin-top:1rem}}';
    (document.head || document.documentElement).appendChild(st);
  }

  function watch() {
    document.addEventListener('cart-drawer:render', later);
    document.addEventListener('cart-drawer:update', later);
    document.addEventListener('cart-drawer:open', later);
    document.addEventListener('quantity_break:changed', later);
    window.addEventListener('pageshow', later);
    window.addEventListener('load', later);
    /* drawer or /cart markup swapped in by cart.js, a price rewritten by the quantity break */
    if (window.MutationObserver && document.body) {
      obs = new MutationObserver(function (recs) {
        var u = 0;
        for (var i = 0; i < recs.length && u < 2; i++) u = Math.max(u, urgency(recs[i]));
        if (u === 2) now(); else if (u === 1) later();
      });
      obs.observe(document.body, { childList: true, subtree: true, characterData: true });
    }
  }
  function go() {
    if (active || failed) return;
    active = true;
    try {
      addStyle();
      apply();
      if (active) watch();
    } catch (e) { fail(); }
  }

  /* Shopify sets Shopify.currency.active to the currency the page's prices are in. It is USD on this store (no
     Shopify Payments, so no presentment currencies). If that ever changes, a "$48.37" may be Canadian dollars and
     converting it as USD would be wrong (and the USD note untrue): show nothing. Missing or unreadable: as USD. */
  function usdPage() {
    try {
      var c = window.Shopify && window.Shopify.currency && window.Shopify.currency.active;
      return !c || String(c).toUpperCase() === 'USD';
    } catch (e) { return false; }
  }

  function begin(detected) {
    if (started) return;
    started = true;
    try {
      if (!usdPage()) return;                               /* prices not in USD: nothing */
      country = pick(detected);
      var cur = CURRENCY[country] || '';
      if (!cur) return;                                     /* US or unknown: nothing */
      var f = makeFormat(LOCALE[country] || 'en', cur);
      if (!f) return;
      getRate(cur, function (r) {
        if (!r) return;                                     /* no rate: nothing */
        currency = cur;
        rate = r;
        nf = f;
        if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', go);
        else go();
      });
    } catch (e) { fail(); }
  }

  window.xvLocalCurrency = {
    version: '2026-10-11-v5.2',
    fallbackDate: FALLBACK_DATE,
    apply: apply,
    country: function () { return country; },
    currency: function () { return active ? currency : ''; },
    rate: function () { return active ? rate : 0; },
    isActive: function () { return active; },
    format: function (v) { return active ? label(Number(v) || 0) : ''; }
  };

  var gate = window.xvCardGate;
  if (gate && typeof gate.onSettle === 'function') {
    gate.onSettle(begin);
  } else {
    /* the gate is missing or older: its cache only, never a request */
    begin(sget('xv_card_geo'));
  }
})();